import axios from 'axios';

// The backend caps `limit` at 500 for GET /api/payments/c2b.
export const C2B_EXPORT_PAGE_SIZE = 500;

// Safety ceiling so an unfiltered export can't spin forever / exhaust memory.
export const C2B_EXPORT_MAX_ROWS = 20000;

export const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
};

const formatExportDateTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const matchLabel = (value) => (value === 'matched' ? 'Matched' : 'Unmatched');
const postLabel = (value) => (value === 'posted' ? 'Posted' : 'Pending');

/**
 * Page through GET /api/payments/c2b collecting every row that matches `params`
 * (the same filter params the list view uses). Stops at C2B_EXPORT_MAX_ROWS.
 *
 * @returns {{ rows: object[], total: number, truncated: boolean }}
 */
export const fetchAllC2BTransactions = async ({ baseUrl, headers, params = {} }) => {
    const rows = [];
    let offset = 0;
    let total = 0;
    let truncated = false;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const search = new URLSearchParams({ ...params, limit: C2B_EXPORT_PAGE_SIZE, offset });
        const res = await axios.get(`${baseUrl}/api/payments/c2b?${search}`, { headers });

        if (res.data?.success === false) {
            throw new Error(res.data?.message || 'Failed to load C2B transactions.');
        }

        const pageRows = Array.isArray(res.data?.data) ? res.data.data : [];
        total = Number(res.data?.total) || 0;
        rows.push(...pageRows);

        if (rows.length >= C2B_EXPORT_MAX_ROWS && rows.length < total) {
            truncated = true;
            break;
        }

        if (pageRows.length < C2B_EXPORT_PAGE_SIZE || rows.length >= total) {
            break;
        }

        offset += C2B_EXPORT_PAGE_SIZE;
    }

    return { rows: rows.slice(0, C2B_EXPORT_MAX_ROWS), total, truncated };
};

const EXPORT_COLUMNS = [
    ['Date & Time', (row) => formatExportDateTime(row.trans_time)],
    ['Receipt', (row) => row.trans_id || ''],
    ['Amount', (row) => (row.amount === null || row.amount === undefined ? '' : Number(row.amount).toFixed(2))],
    ['Bill Ref', (row) => row.bill_ref_raw || ''],
    ['Plate', (row) => row.plate_no || ''],
    ['Payer', (row) => [row.first_name, row.last_name].filter(Boolean).join(' ')],
    ['Match', (row) => matchLabel(row.match_status)],
    ['Car Post', (row) => postLabel(row.post_status)],
    ['Cashbook', (row) => postLabel(row.cb_posted)],
];

/** Build a CSV string mirroring the on-screen C2B transactions table. */
export const buildC2BTransactionsCsv = (rows = []) => {
    const header = EXPORT_COLUMNS.map(([label]) => escapeCsvValue(label)).join(',');
    const body = rows.map((row) => (
        EXPORT_COLUMNS.map(([, accessor]) => escapeCsvValue(accessor(row))).join(',')
    ));
    return [header, ...body].join('\n');
};

/** Trigger a browser download of `csv` as `filename`. */
export const downloadCsv = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
